import { Form, Input, Select, Button, message, Modal, Divider, Space, Row, Col } from "antd";
import { CloseOutlined, PlusOutlined, EditOutlined } from "@ant-design/icons";
import { useEffect, useState, useRef } from "react";
import AnimateItems from "../../../utils/AnimateItems";
import axios from "axios";
import PersonaForm from "./persona/PersonaForm";
import { scian } from "./formOptions";
import { useAuth } from "../../../layout/auth";

// The set of fields on the verification record that describe "whoever PV
// currently points to" — they only make sense together, so they're
// snapshotted and reverted as one unit. Assumption: ClasificacionSCIANID
// and NombreComercial are treated as part of this same group, the same
// way CargoPA was for PAInfoForm — flag if these should stay independent
// of the persona selection instead.
const PV_FIELDS = [
  "PV",
  "DatoPV",
  "TelefonoFijoPV",
  "TelefonoMovilPV",
  "CorreoElectronicoPV",
  "ClasificacionSCIANID",
  "NombreComercial",
];

const pickPVFields = (source) =>
  PV_FIELDS.reduce((acc, field) => {
    acc[field] = source?.[field] ?? null;
    return acc;
  }, {});

export default function PVInfoForm(props) {
  const { title, visible, mainData: verification, setMainData: setVerification, setVisible } = props;
  const [personaFormVisible, setPersonaFormVisible] = useState(false);
  const [datosPersona, setDatosPersona] = useState();
  const [telefonosFijos, setTelefonosFijos] = useState();
  const [telefonosMoviles, setTelefonosMoviles] = useState();
  const [correosPersona, setCorreosPersona] = useState();
  const [personasList, setPersonasList] = useState();
  const [openDropdown, setOpenDropdown] = useState(false);
  const [personaEditing, setPersonaEditing] = useState();
  const auth = useAuth();

  // Snapshot of the PV-related fields exactly as they were the moment the
  // modal opened, so "Cancelar" can genuinely revert to this — including
  // undoing an already-saved new persona created during this session.
  const originalPVDataRef = useRef();

  const [form] = Form.useForm();

  useEffect(() => {
    if (visible) {
      originalPVDataRef.current = pickPVFields(verification);
    }
    // Deliberately only depends on `visible`: we want this captured once,
    // at the moment the modal opens, not every time `verification` changes
    // afterward (which happens as soon as a persona auto-saves).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // Extracted so it can be reused both on initial load and right after a
  // new persona is created (so the dropdown includes them immediately).
  // Note: unlike PF/PA/PS, PV does NOT filter by TipoPersonaID, since a
  // "Razón social" can be any persona type here.
  const fetchPersonasList = () => {
    const personasPromise =
      auth.Rol === "director"
        ? axios.get(`${process.env.REACT_APP_API_URL}/personas`)
        : axios.get(`${process.env.REACT_APP_API_URL}/personas/usuario/${auth.UID}`);

    return personasPromise
      .then((p) => {
        setPersonasList(p.data);
      })
      .catch((error) => {
        console.log(error);
        message.error(error.message);
      });
  };

  // The single source of truth for persisting this verificación. Used by
  // the main "Guardar" button, immediately after a new persona is created,
  // and by "Cancelar" (with an override snapshot) to genuinely revert.
  const saveVerification = (overrides) => {
    const verificationData = { ...verification, ...form.getFieldsValue(), ...overrides };
    return axios
      .put(`${process.env.REACT_APP_API_URL}/verificaciones/${verification.VerificacionID}`, verificationData)
      .then(() =>
        axios.get(`${process.env.REACT_APP_API_URL}/verificaciones/${verification.VerificacionID}`).then((res) => {
          setVerification(res.data[0]);
          return true;
        })
      )
      .catch((err) => {
        console.log(err);
        message.error("Error al actualizar la verificación");
        return false;
      });
  };

  // Extracted so it can be reused by the Select's onChange and right after
  // a new persona is created. Returns a promise so callers can chain a
  // save immediately after the related data has finished loading.
  const handlePersonaSelected = (value) => {
    if (value) {
      return Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/datos/razonsocial/${value}`),
        axios.get(`${process.env.REACT_APP_API_URL}/telefonos/persona/${value}`),
        axios.get(`${process.env.REACT_APP_API_URL}/correos/persona/${value}`),
      ])
        .then(([d, t, c]) => {
          setDatosPersona(d.data);
          setTelefonosFijos(t.data.filter((telefono) => telefono.TipoTelefonoID === 2));
          setTelefonosMoviles(t.data.filter((telefono) => telefono.TipoTelefonoID === 1));
          setCorreosPersona(c.data);
          form.setFieldsValue({
            DatoPV: d.data[0]?.DatoID,
            TelefonoFijoPV: t.data.filter((telefono) => telefono.TipoTelefonoID === 2)[0]?.TelefonoID,
            TelefonoMovilPV: t.data.filter((telefono) => telefono.TipoTelefonoID === 1)[0]?.TelefonoID,
            CorreoElectronicoPV: c.data[0]?.CorreoElectronicoID,
          });
        })
        .catch((error) => {
          console.log(error);
          message.error("Error al cargar los datos");
        });
    } else {
      setDatosPersona(null);
      setTelefonosFijos(null);
      setTelefonosMoviles(null);
      setCorreosPersona(null);
      form.setFieldsValue({
        DatoPV: null,
        TelefonoFijoPV: null,
        TelefonoMovilPV: null,
        CorreoElectronicoPV: null,
      });
      return Promise.resolve();
    }
  };

  // Called by PersonaForm right after a brand-new persona is saved:
  // select them, load their (empty) data, and then IMMEDIATELY persist the
  // link to this verificación (the same save the "Guardar" button does) —
  // so the persona is genuinely linked in the database right away, and the
  // next fetch of personasList will legitimately include them. If the user
  // later clicks "Cancelar", this save gets reverted via originalPVDataRef.
  const handlePersonaCreated = (newPersonaId) => {
    form.setFieldsValue({ PV: newPersonaId });
    handlePersonaSelected(newPersonaId).then(() => {
      saveVerification().then((ok) => {
        if (ok) {
          fetchPersonasList();
          message.success("La nueva persona quedó guardada y vinculada a esta verificación.");
        }
      });
    });
  };

  // Called by PersonaForm right after a teléfono is added to the persona
  // currently being edited: select it in the matching field and confirm.
  const handleTelefonoAdded = (newTelefonoId, tipoTelefonoId) => {
    if (tipoTelefonoId === 2) {
      form.setFieldsValue({ TelefonoFijoPV: newTelefonoId });
      message.success("El nuevo teléfono fijo ya quedó seleccionado en el formulario.");
    } else if (tipoTelefonoId === 1) {
      form.setFieldsValue({ TelefonoMovilPV: newTelefonoId });
      message.success("El nuevo teléfono móvil ya quedó seleccionado en el formulario.");
    }
  };

  // Called by PersonaForm right after a correo is added to the persona
  // currently being edited: select it and confirm.
  const handleCorreoAdded = (newCorreoId) => {
    form.setFieldsValue({ CorreoElectronicoPV: newCorreoId });
    message.success("El nuevo correo ya quedó seleccionado en el formulario.");
  };

  // Called by PersonaForm right after a dato/identificación is added or
  // edited for the persona currently being edited: refresh the datos list
  // and select it — but ONLY if the new/edited dato's type is actually
  // eligible here. Uses the razonsocial endpoint, matching this screen's
  // own query. /datos/razonsocial/:id has its own TipoDatoID/TipoPersonaID
  // rules (see datos.js) — e.g. for a TipoPersonaID 1 persona, only 1, 2,
  // 5, 6, 8, 9, 10 qualify, and for TipoPersonaID 2 only 11 (RFC)
  // qualifies. If the new/edited dato isn't in the list, leave the
  // current selection untouched instead of forcing in an ID with no
  // matching option.
  const handleDatoAdded = (newDatoId) => {
    if (!personaEditing) return;
    axios
      .get(`${process.env.REACT_APP_API_URL}/datos/razonsocial/${personaEditing}`)
      .then((d) => {
        setDatosPersona(d.data);
        if (d.data.some((item) => item.DatoID === newDatoId)) {
          form.setFieldsValue({ DatoPV: newDatoId });
          message.success("El nuevo dato ya quedó seleccionado en el formulario.");
        } else {
          message.warning(
            "El tipo de identificación que agregaste no aplica para \"Forma de identificación de la Razón social\"."
          );
        }
      })
      .catch((error) => {
        console.log(error);
        message.error("Error al actualizar los datos de la razón social");
      });
  };

  // "Cancelar" reverts the PV-related fields back to exactly what they
  // were when the modal opened — genuinely writing that back to the
  // database, since a persona created during this session may have
  // already been auto-saved and linked.
  const handleCancel = () => {
    saveVerification(originalPVDataRef.current).then((ok) => {
      if (ok) setVisible(false);
    });
  };

  useEffect(() => {
    visible &&
      Promise.all([
        fetchPersonasList(),
        axios.get(`${process.env.REACT_APP_API_URL}/datos/razonsocial/${verification?.PV}`),
        axios.get(`${process.env.REACT_APP_API_URL}/telefonos/persona/${verification?.PV}`),
        axios.get(`${process.env.REACT_APP_API_URL}/correos/persona/${verification?.PV}`),
      ])
        .then(([, d, t, c]) => {
          setDatosPersona(d.data);
          setTelefonosFijos(t.data.filter((telefono) => telefono.TipoTelefonoID === 2));
          setTelefonosMoviles(t.data.filter((telefono) => telefono.TipoTelefonoID === 1));
          setCorreosPersona(c.data);
          form.setFieldsValue(verification);
        })
        .catch((error) => {
          console.log(error);
          message.error(error.message);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verification, form, visible]);

  const clearSelectProps = {
    style: { width: "100%" },
    allowClear: true,
    clearIcon: <CloseOutlined style={{ color: "red" }} />,
    showSearch: true,
    filterOption: (input, option) => option.children?.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0,
  };

  const colResposivity = {
    xs: 24,
    sm: 24,
    md: 12,
    lg: 12,
    xl: 12,
    xxl: 12,
  };

  return (
    <>
      <Modal title={title} visible={visible} onCancel={() => setVisible(false)} footer={null} width={800} centered>
        <Form form={form} layout="vertical">
          <AnimateItems delay={0.15}>
            <Row gutter={[40, 0]}>
              <Col {...colResposivity}>
                <Form.Item name="PV" label="Razón social">
                  <Select
                    onChange={handlePersonaSelected}
                    {...clearSelectProps}
                    open={openDropdown}
                    onDropdownVisibleChange={(visibility) => setOpenDropdown(visibility)}
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <div style={{ float: "right" }}>
                          <Divider style={{ margin: "4px 0" }} />
                          <Space direction="horizontal">
                            <Button
                              icon={<EditOutlined />}
                              onClick={() => {
                                setPersonaEditing(form.getFieldValue("PV"));
                                setOpenDropdown(false);
                                setTimeout(() => {
                                  setPersonaFormVisible(true);
                                }, 250);
                              }}
                            >
                              Editar persona
                            </Button>
                            <Button
                              icon={<PlusOutlined />}
                              onClick={() => {
                                setPersonaEditing(null);
                                setOpenDropdown(false);
                                setTimeout(() => {
                                  setPersonaFormVisible(true);
                                }, 250);
                              }}
                            >
                              Agregar persona
                            </Button>
                          </Space>
                        </div>
                      </>
                    )}
                  >
                    {personasList?.map((persona) => (
                      <Select.Option key={persona.PersonaID} value={persona.PersonaID}>
                        {persona.NombreCompleto}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="DatoPV" label="Forma de identificación de la Razón social">
                  <Select {...clearSelectProps}>
                    {datosPersona?.map((dato) => (
                      <Select.Option key={dato.DatoID} value={dato.DatoID}>
                        {dato.TipoDato_Corto + ": " + dato.Dato}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Row gutter={[16, 0]}>
                  <Col span={12}>
                    <Form.Item name="TelefonoFijoPV" label="Teléfono fijo">
                      <Select {...clearSelectProps}>
                        {telefonosFijos?.map((telefono) => (
                          <Select.Option key={telefono.TelefonoID} value={telefono.TelefonoID}>
                            {telefono.Telefono}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="TelefonoMovilPV" label="Teléfono móvil">
                      <Select {...clearSelectProps}>
                        {telefonosMoviles?.map((telefono) => (
                          <Select.Option key={telefono.TelefonoID} value={telefono.TelefonoID}>
                            {telefono.Telefono}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="CorreoElectronicoPV" label="Correo electrónico">
                  <Select {...clearSelectProps}>
                    {correosPersona?.map((correo) => (
                      <Select.Option key={correo.CorreoElectronicoID} value={correo.CorreoElectronicoID}>
                        {correo.CorreoElectronico}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col {...colResposivity}>
                <Form.Item name="ClasificacionSCIANID" label="Clasificación SCIAN">
                  <Select {...clearSelectProps}>
                    {scian.map((tipo) => (
                      <Select.Option key={tipo.value} value={tipo.value}>
                        {tipo.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="NombreComercial" label="Nombre comercial">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </AnimateItems>
        </Form>
        <PersonaForm
          setPersonasList={setPersonasList}
          setDatosPersona={setDatosPersona}
          setTelefonosFijos={setTelefonosFijos}
          setTelefonosMoviles={setTelefonosMoviles}
          setCorreosPersona={setCorreosPersona}
          personaEditing={personaEditing}
          setPersonaEditing={setPersonaEditing}
          personaFormVisible={personaFormVisible}
          setPersonaFormVisible={setPersonaFormVisible}
          refreshPersonasList={fetchPersonasList}
          onPersonaCreated={handlePersonaCreated}
          onTelefonoAdded={handleTelefonoAdded}
          onCorreoAdded={handleCorreoAdded}
          onDatoChanged={handleDatoAdded}
          onCancel={() => setPersonaFormVisible(false)}
        />
        <Button
          type="primary"
          onClick={() => {
            saveVerification().then((ok) => {
              if (ok) setVisible(false);
            });
          }}
        >
          Guardar
        </Button>
        <Button onClick={handleCancel}>Cancelar</Button>
      </Modal>
    </>
  );
}